import type { TenantBootstrap } from "../tenant/bootstrap.js";
import { keycloakBaseUrl } from "./keycloak.js";
import { webglAuthCallbackUrl } from "./webgl-auth-callback.js";
import { isWebglAuthMessage, type WebglAuthMessage } from "./webgl-auth-message.js";

/**
 * The OPENER half of the panel's sign-in (ADR-050 D2–D4).
 *
 * The panel cannot redirect itself — it is someone else's frame, and `main.tsx` refuses to navigate it
 * (ADR-044 D6). It cannot authenticate silently either: Keycloak is cross-SITE to every one of our app origins
 * (`my-prod.create.at` vs `*.arcreator.at` / `kdb-linz.at`), so `check-sso`, a hidden `prompt=none` iframe and the
 * login-status iframe are all third-party requests and all blocked (ADR-050 §3).
 *
 * So: a popup, on an explicit user gesture. A popup is a new TOP-LEVEL browsing context, which makes Keycloak's
 * SSO cookie first-party again. In practice the author reached Unity minutes earlier through Unity's own Keycloak
 * redirect, so the popup hits an existing SSO session, bounces straight back and closes — one click, no form.
 *
 * **The cryptography lives HERE, in the document that survives the flow.** The popup is a relay that holds
 * nothing (`webgl-auth-callback.ts`). This module generates the verifier, keeps it in a local variable for the
 * duration of one call, and exchanges the code itself. Nothing is written to any storage API, and the verifier is
 * unreachable from the popup — so the code the popup carries is worthless on its own.
 */

/** Why a sign-in did not produce tokens. Mapped to a translated message by the caller; never a raw string. */
export type WebglSignInFailure =
  | "popup-blocked"
  | "cancelled"
  | "state-mismatch"
  | "denied"
  | "exchange-failed"
  | "timeout";

export class WebglSignInError extends Error {
  constructor(
    readonly reason: WebglSignInFailure,
    /** Keycloak's `error_description` when it supplied one. Display-only. */
    readonly detail?: string,
  ) {
    super(reason);
    this.name = "WebglSignInError";
  }
}

/** What the exchange yields. Handed straight to `adoptSession` and never stored. */
export interface WebglTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

/** Give up on a popup nobody is driving. A bound, not a retry — nothing re-opens (ADR-050 D6). */
const SIGN_IN_TIMEOUT_MS = 5 * 60_000;
/** How often to notice the user closed the popup. Cheap, and the only way: there is no `close` event for openers. */
const CLOSED_POLL_MS = 400;
/**
 * After a closed popup is first observed, how long to keep listening before calling it a cancel.
 *
 * Belt-and-braces for the diff panel's race. The relay no longer self-closes, so a closed popup already means the
 * user closed it — but a manual close landing in the instant between the relay's `postMessage` and our task
 * draining it would still discard a valid code. `postMessage` is a queued task, so one short window is enough,
 * and the cost of being wrong in the other direction is a quarter-second before an error the user is expecting.
 */
const CLOSED_GRACE_MS = 250;

/**
 * Run the whole flow: generate PKCE, open the popup, await the code, exchange it.
 *
 * MUST be called from a user gesture — `window.open` is blocked otherwise, which surfaces as `popup-blocked`
 * rather than as a hang.
 */
export function signInViaPopup(tenant: TenantBootstrap, win: Window = window): Promise<WebglTokens> {
  return signInViaPopupAt(
    { baseUrl: keycloakBaseUrl(), realm: tenant.realm, clientId: tenant.clientId, ...(tenant.idpHint ? { idpHint: tenant.idpHint } : {}) },
    win,
  );
}

/**
 * Where a popup sign-in goes: a Keycloak base URL (`…/auth`), a realm and a PUBLIC client. The app's own for the panel;
 * Unity's for the editor's 3D test on staging (ADR-058 D9), whose Keycloak is not ours there.
 */
export interface PopupSignInTarget {
  baseUrl: string;
  realm: string;
  clientId: string;
  idpHint?: string;
}

/** `signInViaPopup` against any Keycloak. Same rules: call it from a user gesture. */
export async function signInViaPopupAt(target: PopupSignInTarget, win: Window = window): Promise<WebglTokens> {
  // ── OPEN FIRST, NAVIGATE SECOND — and the order is load-bearing ──────────────────────────────────────────
  // `window.open` is permitted only under a transient user activation, and computing the PKCE challenge means
  // `await crypto.subtle.digest(...)`. Opening after that await leaves the activation behind: Chrome's window is
  // time-based and usually forgives it, Safari does not, so the popup would be blocked for some users and not
  // others — the worst kind of bug to reproduce. So the window is claimed synchronously inside the click, while
  // the activation is unambiguous, and pointed at the authorize URL once the challenge exists.
  //
  // `about:blank` inherits the opener's origin, so the later `location.replace` is an ordinary same-origin
  // navigation that we are allowed to perform on a window we opened.
  //
  // Named, so a second click re-uses the same window instead of stacking popups.
  const popup = win.open("", "arc-webgl-signin", popupFeatures(win));
  if (!popup) throw new WebglSignInError("popup-blocked");

  try {
    const verifier = randomUrlSafe(64);
    const state = randomUrlSafe(16);
    const redirectUri = webglAuthCallbackUrl(win.location.href);
    const challenge = await codeChallenge(verifier);
    // `replace`, not `href`: the blank step must not become a history entry the user can go "back" into.
    popup.location.replace(buildAuthorizeUrl({ target, redirectUri, state, challenge }));

    const message = await awaitCallback(popup, state, win);
    if (message.error) throw new WebglSignInError("denied", message.errorDescription);
    if (!message.code) throw new WebglSignInError("exchange-failed");

    return await exchangeCode({ target, code: message.code, redirectUri, verifier });
  } catch (error) {
    // A blank popup left on screen after a failure is worse than no popup: it looks like the flow is still going.
    try {
      if (!popup.closed) popup.close();
    } catch {
      /* already gone */
    }
    throw error;
  }
}

/**
 * Wait for the relay, validating three things in order before the value is used at all:
 *
 *  1. **`event.source === popup`** — identity of the sender's window. Origin alone is NOT enough: any same-origin
 *     frame can post, and on the webgl route we are by definition running inside someone else's page.
 *  2. **`event.origin === our origin`** — the popup ends up back on our origin, so anything else is not it.
 *  3. **`state` matches the value generated above** — ties this response to this request, so a stale popup from an
 *     earlier attempt cannot resolve a later one.
 *
 * A message failing any check is IGNORED rather than rejected: `window.message` is a shared bus, and the Unity
 * bridge is posting on it too. Rejecting on the first stranger would make the Unity host's own traffic cancel our
 * sign-in.
 */
function awaitCallback(popup: Window, expectedState: string, win: Window): Promise<WebglAuthMessage> {
  return new Promise<WebglAuthMessage>((resolve, reject) => {
    const origin = win.location.origin;
    let settled = false;

    let grace: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      settled = true;
      win.removeEventListener("message", onMessage);
      clearInterval(closedPoll);
      clearTimeout(timeout);
      if (grace !== undefined) clearTimeout(grace);
    };
    /**
     * Closing is the OPENER's job (`webgl-auth-callback.ts`): the relay used to close itself one line after
     * posting, and because delivery is a queued task while `closed` flips synchronously, the poll below could see
     * a closed popup before the message arrived — reporting a completed sign-in as "cancelled".
     */
    const closePopup = () => {
      try {
        if (!popup.closed) popup.close();
      } catch {
        /* cross-origin at this instant — nothing we can do, and nothing that matters */
      }
    };
    const fail = (reason: WebglSignInFailure) => {
      if (settled) return;
      cleanup();
      closePopup(); // leave no orphan window behind on the failure paths we own
      reject(new WebglSignInError(reason));
    };

    function onMessage(event: MessageEvent): void {
      if (settled) return;
      if (event.source !== popup) return;
      if (event.origin !== origin) return;
      if (!isWebglAuthMessage(event.data)) return;
      // Reached only by a message from OUR popup on OUR origin, so a mismatch here is a real desync, not noise:
      // fail loudly rather than keep waiting for a better one that is not coming.
      if (event.data.state !== expectedState) {
        fail("state-mismatch");
        return;
      }
      const data = event.data;
      cleanup();
      closePopup();
      resolve(data);
    }

    const closedPoll = setInterval(() => {
      if (!popup.closed) return;
      // Seen closed — but do NOT conclude "cancelled" here. Stop polling and keep the listener up for one grace
      // window, so a message already queued at the moment of the close still wins. Only silence is a cancel.
      clearInterval(closedPoll);
      grace = setTimeout(() => fail("cancelled"), CLOSED_GRACE_MS);
    }, CLOSED_POLL_MS);

    const timeout = setTimeout(() => fail("timeout"), SIGN_IN_TIMEOUT_MS);

    win.addEventListener("message", onMessage);
  });
}

function buildAuthorizeUrl(o: {
  target: PopupSignInTarget;
  redirectUri: string;
  state: string;
  challenge: string;
}): string {
  const u = new URL(`${realmBase(o.target)}/protocol/openid-connect/auth`);
  u.searchParams.set("client_id", o.target.clientId);
  u.searchParams.set("redirect_uri", o.redirectUri);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("response_mode", "query");
  u.searchParams.set("scope", "openid");
  u.searchParams.set("state", o.state);
  u.searchParams.set("code_challenge", o.challenge);
  u.searchParams.set("code_challenge_method", "S256");
  // The hint is what actually routes a federated tenant to its IdP. `keycloak.ts` documents why it must be passed
  // on the login URL and not to `init()`: the adapter copies only `prompt` and `locale` from init options.
  if (o.target.idpHint) u.searchParams.set("kc_idp_hint", o.target.idpHint);
  return u.toString();
}

/**
 * Trade the code for tokens. The verifier is sent here and nowhere else, which is what makes the intercepted-code
 * case uninteresting.
 *
 * A public client, so no secret — the PKCE verifier IS the proof. Failures are collapsed to one opaque reason:
 * Keycloak's body distinguishes `invalid_grant` from `invalid_client`, and echoing that to the UI tells an
 * attacker which half they got wrong. Nothing from the request or the response is logged.
 */
async function exchangeCode(o: {
  target: PopupSignInTarget;
  code: string;
  redirectUri: string;
  verifier: string;
}): Promise<WebglTokens> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: o.target.clientId,
    code: o.code,
    redirect_uri: o.redirectUri,
    code_verifier: o.verifier,
  });

  return tokenRequest(o.target, body);
}

/**
 * Renew with the refresh token (`grant_type=refresh_token`, same public client). Used by the 3D test's own token holder
 * (ADR-058 D9); the panel refreshes through the adapter instead. Any refusal is the same opaque `exchange-failed`.
 */
export function refreshTokensAt(target: PopupSignInTarget, refreshToken: string): Promise<WebglTokens> {
  return tokenRequest(target, new URLSearchParams({ grant_type: "refresh_token", client_id: target.clientId, refresh_token: refreshToken }));
}

async function tokenRequest(target: PopupSignInTarget, body: URLSearchParams): Promise<WebglTokens> {
  let payload: unknown;
  try {
    const res = await fetch(`${realmBase(target)}/protocol/openid-connect/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!res.ok) throw new Error("token endpoint rejected the exchange");
    payload = await res.json();
  } catch {
    throw new WebglSignInError("exchange-failed");
  }

  const t = payload as { access_token?: unknown; refresh_token?: unknown; id_token?: unknown };
  // Both are REQUIRED: without a refresh token the panel would hold a session that dies in minutes with no way to
  // renew it, which reads to the user as a random logout mid-edit.
  if (typeof t.access_token !== "string" || typeof t.refresh_token !== "string") {
    throw new WebglSignInError("exchange-failed");
  }
  return {
    token: t.access_token,
    refreshToken: t.refresh_token,
    ...(typeof t.id_token === "string" ? { idToken: t.id_token } : {}),
  };
}

/** `<base>/realms/<realm>` — the same composition `initAuth` hands to the adapter. */
function realmBase(target: PopupSignInTarget): string {
  return `${target.baseUrl.replace(/\/+$/, "")}/realms/${encodeURIComponent(target.realm)}`;
}

/** Right-aligned and roughly form-shaped, so it reads as a dialog rather than as a second app window. */
function popupFeatures(win: Window): string {
  const width = 520;
  const height = 720;
  const left = (win.screenX || 0) + Math.max(0, win.innerWidth - width);
  const top = win.screenY || 0;
  return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
}

/** Cryptographically random, base64url — never `Math.random`, which is predictable and would defeat PKCE. */
function randomUrlSafe(bytes: number): string {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return base64Url(b);
}

async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
